/**
 * Adobe `.cube` 3D LUT 解析（M4-03 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 规则 10（CLAUDE.md）：导入解析器一律防御性——.cube 是用户可以随手拖进来的
 * 文本文件，字段数量、尺寸、数值范围全部来自不可信输入，任何一项不符都要
 * 明确报错，不能静默截断或用垃圾数据填充。
 *
 * 分层（CLAUDE.md 规则 4）：I/O 是最底层，不能 import engine 的 `Lut3D` 类型——
 * 这里的 `ParsedCube` 结构与之字段相同，engine 层可以直接结构兼容地当 `Lut3D` 用。
 */

const MIN_SIZE = 2;
const MAX_SIZE = 256;

/** 解析出的 3D LUT（字段与 `engine/adjustments/lut3d.ts` 的 `Lut3D` 结构兼容）。 */
export interface ParsedCube {
  readonly size: number;
  readonly data: Float32Array;
  readonly domainMin: readonly [number, number, number];
  readonly domainMax: readonly [number, number, number];
}

/** 解析 .cube 文本。格式不对/尺寸越界/数据行数不符都抛 `RangeError`。 */
export function parseCubeFile(text: string): ParsedCube {
  let size: number | null = null;
  let domainMin: [number, number, number] = [0, 0, 0];
  let domainMax: [number, number, number] = [1, 1, 1];
  const rows: number[][] = [];

  const lines = text.split(/\r\n|\r|\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0 || line.startsWith('#')) continue;

    if (line.startsWith('TITLE')) continue; // 标题，忽略

    if (line.startsWith('LUT_3D_SIZE')) {
      const value = Number(line.slice('LUT_3D_SIZE'.length).trim());
      if (!Number.isInteger(value) || value < MIN_SIZE || value > MAX_SIZE) {
        throw new RangeError(`.cube 的 LUT_3D_SIZE 必须是 ${MIN_SIZE}–${MAX_SIZE} 的整数，收到 ${line}。`);
      }
      size = value;
      continue;
    }

    if (line.startsWith('DOMAIN_MIN')) {
      domainMin = parseTriple(line, 'DOMAIN_MIN');
      continue;
    }
    if (line.startsWith('DOMAIN_MAX')) {
      domainMax = parseTriple(line, 'DOMAIN_MAX');
      continue;
    }

    if (line.startsWith('LUT_1D_SIZE')) {
      throw new RangeError('.cube 是 1D LUT，Photosmith 目前只支持 3D LUT（LUT_3D_SIZE）。');
    }

    // 其余非空行视为数据行：3 个浮点数
    const parts = line.split(/\s+/);
    if (parts.length !== 3) {
      throw new RangeError(`.cube 数据行必须恰好 3 个数，收到「${line}」。`);
    }
    const row = parts.map(Number);
    if (row.some((v) => !Number.isFinite(v))) {
      throw new RangeError(`.cube 数据行含非法数值：「${line}」。`);
    }
    rows.push(row);
  }

  if (size === null) {
    throw new RangeError('.cube 文件缺少 LUT_3D_SIZE。');
  }
  const expected = size * size * size;
  if (rows.length !== expected) {
    throw new RangeError(`.cube 数据行数不符：LUT_3D_SIZE=${size} 需要 ${expected} 行，实际 ${rows.length} 行。`);
  }

  const data = new Float32Array(expected * 3);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    data[i * 3] = row[0]!;
    data[i * 3 + 1] = row[1]!;
    data[i * 3 + 2] = row[2]!;
  }

  return { size, data, domainMin, domainMax };
}

function parseTriple(line: string, keyword: string): [number, number, number] {
  const parts = line.slice(keyword.length).trim().split(/\s+/);
  if (parts.length !== 3) {
    throw new RangeError(`${keyword} 需要 3 个数，收到「${line}」。`);
  }
  const values = parts.map(Number);
  if (values.some((v) => !Number.isFinite(v))) {
    throw new RangeError(`${keyword} 含非法数值：「${line}」。`);
  }
  return [values[0]!, values[1]!, values[2]!];
}
